import { Injectable } from '@angular/core';
import { Rule } from '../rule-base';
import { ModifierContext, ClaimLine, CoreModifier } from '../modifier-models';
import { RuleProvider } from './base-rule-provider';
import { Rule0005 } from '../rules/rule-0005-multiple-procedures';
import { Rule0004 } from '../rules/rule-0004-in-rooms';
import { Rule0011 } from '../rules/rule-0011-emergency';
import { Rule0013 } from '../rules/rule-0013-endoscopy-reduce';
import { RuleAnaesMin } from '../rules/rule-anaesthetic-core';
import {
    AddFixedUnitsRule,
    PercentReduceRule,
    InfoValidatorRule
} from '../rules/rules-generic';
import { RulePolyTrauma } from '../rules/rule-0060-polytrauma';
import { RuleBmiNeonate50 } from '../rules/rule-0018-0019-bmi-neonate';
import { Rule0010LocalAnaes } from '../rules/rule-0010-local-anaesthetic';
import { RuleBothFeet } from '../rules/rule-0057-both-feet';
import { RuleMultiCath6305 } from '../rules/rule-6305-multi-cath';
import { Rule0029AssistantAnaes } from '../rules/rule-0029-assistant-anaes';
import { Rule0046FractureFollowUp } from '../rules/rule-0046-fracture-followup';
import { Rule0063TwoSpecialists } from '../rules/rule-0063-two-specialists';
import { Rule0065SpineRepeat } from '../rules/rule-0065-spine-repeat';
import { AddPercentageRule } from '../rules/rules-percentage';
import { RuleAssistantsCompound } from '../rules/rule-assistants-compound';
import { MEDPRAX_MEDICAL_DISCIPLINES } from '../../data/app.data';

@Injectable({ providedIn: 'root' })
export class MedicalRuleProvider implements RuleProvider {
    readonly disciplineCode = 'medical';
    readonly disciplineName = 'Medical Professionals';

    // Medical discipline codes that use medical rules: every discipline on MedPrax's medical tariff
    private readonly medicalDisciplines = MEDPRAX_MEDICAL_DISCIPLINES;

    getRules(): Rule[] {
        return [
            // ── complex / reduction rules first ────────────────────────────────
            new Rule0005(),
            new RuleAnaesMin(),
            new Rule0013(),
            new Rule0010LocalAnaes(),
            new RuleBmiNeonate50(),
            new RuleBothFeet(),
            new RulePolyTrauma(),
            new RuleMultiCath6305(),
            new Rule0029AssistantAnaes(),
            new Rule0046FractureFollowUp(),
            new Rule0063TwoSpecialists(),
            new Rule0065SpineRepeat(),

            // ── additive rules (assistants, rooms, emergency) ──────────────────
            new RuleAssistantsCompound(),
            new Rule0004(),
            new Rule0011(),

            // ── percentage **add** rules ──────────────────────────────────────
            ...([
                ['0058', 1],      // +100 %
                ['0066', 0.25],   // +25 %
                ['0067', 0.25],
                ['0069', 0.10],
                ['0073', 1],      // +100 %
                ['0074', 0.3333], // +33 ⅓ %
                ['0078', 0.5],    // +50 %
                ['0160', 0.3],    // +30 %
                ['5104', 0.3],    // +30 %
                ['0084', 0.10],   // film cost +10 %
                ['0099', 0.50]    // STAT basis tests +50 %
            ] as [CoreModifier, number][]).map(([c, p]) => new AddPercentageRule(c, p)),

            // ── additive rules (assistants, rooms, emergency) ──────────────────
            ...([
                ['0053', 32], ['0055', 77], ['0070', 45], ['0090', 30], ['0100', 75],
                ['0007', 15], ['0017', 7.5], ['0026', 3], ['0037', 3], ['0038', 4],
                ['0039', 3], ['0041', 3], ['0042', 3], ['0043', 3], ['0044', 3],
                ['5441', 1], ['5442', 2], ['5443', 3], ['5444', 4], ['5445', 5],
                ['5448', 8], ['0075', 21], ['0165', 6], ['01030', 30]
            ] as [CoreModifier, number][]).map(([c, u]) => new AddFixedUnitsRule(c, u)),

            // ── percentage reductions ─────────────────────────────────────────
            new PercentReduceRule('6101', 2 / 3),
            new PercentReduceRule('0083', 2 / 3),
            new PercentReduceRule('0093', 2 / 3),
            new PercentReduceRule('6102', 0.5),
            new PercentReduceRule('6104', 2 / 3),
            new PercentReduceRule('6107', 0.5),
            new PercentReduceRule('6108', 0.2),
            new PercentReduceRule('6109', 1 / 3),
            new PercentReduceRule('6110', 0.5),
            new PercentReduceRule('6300', 0.5),
            new PercentReduceRule('6301', 0.6),
            new PercentReduceRule('6302', 0.6),
            new PercentReduceRule('6303', 0.55),

            // ── informational validators (no maths) ────────────────────────────
            ...[
                '0001', '0002', '0006', '0014', '0015', '0020', '0021', '0024', '0025', '0027', '0028',
                '0030', '0031', '0033', '0040', '0045', '0047', '0061', '0064', '0072', '0077',
                '0079', '0080', '0081', '0082', '0085', '0086', '0091', '0092', '0095', '0096',
                '0097', '5141', '6100', '6103', '6105', '6106'
            ].map(c => new InfoValidatorRule(c as CoreModifier))
        ];
    }

    canHandle(disciplineCode: string): boolean {
        return this.medicalDisciplines.includes(disciplineCode);
    }

    getContext(lines: ClaimLine[]): ModifierContext {
        // Calculate totals for medical context
        const nonModifierLines = lines.filter(line => !line.isModifier);
        const totalPrice = nonModifierLines.reduce((sum, line) => sum + (line.initialRand || 0), 0);
        const totalUnits = nonModifierLines.reduce((sum, line) => sum + (line.initialUnits || line.units || 0), 0);

        return {
            totalPrice,
            totalUnits,
            // Percentage modifiers (0008/0009/0029) need this to convert their
            // rand share back to units; without it they produce 0 units.
            pricePerUnit: totalUnits > 0 ? totalPrice / totalUnits : 0,
            lines: lines.map(l => ({
                ...l,
                adjustedUnits: l.initialUnits ?? l.units ?? 0,
                adjustedRand: l.initialRand ?? 0,
                trace: ['init']
            }))
        };
    }

    validateModifier(modifierCode: string, line: ClaimLine): boolean {
        // Medical modifiers validation logic
        const medicalModifiers = [
            '0001', '0002', '0004', '0005', '0006', '0007', '0008', '0009', '0010', '0011', '0013', '0014', '0015', '0017', '0018', '0019',
            '0020', '0021', '0024', '0025', '0026', '0027', '0028', '0029', '0030', '0031', '0032', '0033', '0034', '0035', '0036', '0037', '0038', '0039',
            '0040', '0041', '0042', '0043', '0044', '0045', '0046', '0047', '0053', '0055', '0057', '0058', '0060', '0061', '0063', '0064', '0065', '0066', '0067', '0069',
            '0070', '0072', '0073', '0074', '0075', '0077', '0078', '0079', '0080', '0081', '0082', '0083', '0084', '0085', '0086', '0090', '0091', '0092', '0093', '0095', '0096', '0097', '0099',
            '0100', '0160', '0165', '01030', '5104', '5141', '5441', '5442', '5443', '5444', '5445', '5448', '6100', '6101', '6102', '6103', '6104', '6105', '6106', '6107', '6108', '6109', '6110',
            '6300', '6301', '6302', '6303', '6305'
        ];
        
        return medicalModifiers.includes(modifierCode);
    }
} 